// src/components/Login.jsx
//
// Restyled to match CET-HTML-Code/sign-in.html's split-screen layout
// (background image left, form card right) -- all the actual login
// logic below (email/password state, the /login call, the OTP
// redirect, error message) is unchanged from before this restyle.
import React, { useState } from "react";
import axios from "axios";
import { API_ORIGIN, setAuthToken } from "../config/apiConfig";
import { ROUTES } from "../config/routes";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async () => {
    setMsg("");
    if (!email) {
      setMsg("Enter email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_ORIGIN}/api/login`, { email, password });
      if (res.data && res.data.otp_required) {
        localStorage.setItem("login_email", email);
        window.location.href = "/otp";
        return;
      }
      if (res.data && res.data.success) {
        // This app always goes through OTP (see the otp_required branch
        // above) -- this branch is only here in case a future flow ever
        // logs in without OTP. Uses the same authToken key/helper
        // OtpVerify.jsx uses, so apiConfig.js's authFetch()/api instance
        // picks it up automatically either way.
        if (res.data.token) setAuthToken(res.data.token);
        window.location.href = ROUTES.DASHBOARD;
      } else {
        setMsg(res.data?.message || "Login failed");
      }
    } catch (err) {
      console.error(err);
      // FIX: this always showed the generic "Server error" no matter what
      // actually went wrong, which was especially confusing when the real
      // problem was that the browser couldn't reach the backend at all
      // (API server down/unreachable, wrong API_ORIGIN, or no internet
      // connection) -- axios never gets a response back in that case
      // (err.response is undefined; only err.request is set), which is
      // now called out with its own message, per request. A real
      // server-side failure (err.response present, e.g. a 401/500) still
      // shows whatever message the backend sent, falling back to "Server
      // error" only if it didn't send one.
      if (!err.response) {
        setMsg("Network Not Available\nKindly check your internet connection and try again.");
      } else {
        setMsg(err.response.data?.message || "Server error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") login();
  };

  return (
    <div className="auth-box p-0 w-100">
      <div className="row w-100 g-0">
        <div className="col-md-7 d-none d-md-block">
          <div
            className="h-100 position-relative card-side-img rounded-0 overflow-hidden"
            style={{ backgroundImage: "url(/assets/images/logo/CET%20Background-withoutlogo.png)", backgroundSize: "cover", backgroundPosition: "center" }}
          >
            <div className="p-4 card-img-overlay auth-overlay d-flex align-items-end justify-content-center" />
          </div>
        </div>

        <div className="col-md-5">
          <div className="card auth-box-form border-0 mb-0">
            <div className="card-body min-vh-100 position-relative d-flex flex-column justify-content-center">
              <div className="auth-brand mb-3 text-center">
                {/* New brand asset (per request) -- full logo with the
                    dark "COST ESTIMATION TOOL" wordmark, which reads fine
                    here since this card's background is white/light.
                    Was "mb-3 mt-auto" -- the parent is a
                    "d-flex flex-column justify-content-center" filling
                    min-vh-100, so BOTH this logo block and the "Login"
                    block below it had mt-auto, and two auto-margins in a
                    centered flex column each soak up the container's
                    leftover height independently -- that's what pushed
                    them far apart instead of sitting together as one
                    centered group. Dropping mt-auto from both lets them
                    stack tightly; justify-content-center on the parent
                    still centers the pair as a whole. */}
                <img src="/assets/images/logo/CET-Full-logo.png" alt="Cost Estimation Tool" style={{ maxWidth: 220 }} />
              </div>

              <div>
                <div className="text-center">
                  <h3 className="fw-bold text-dark">Login</h3>
                </div>

                {msg && (
                  <div className="alert alert-danger py-2 mt-3 mb-0" role="alert" aria-live="polite">
                    {/* The "Network Not Available" message above is sent as
                        two lines (title \n detail) -- rendered here as a
                        bold heading + a plain detail line. Every other
                        message (missing email, invalid login, a real
                        server error) is a single line and renders as
                        plain text exactly as before. */}
                    {msg.includes("\n") ? (
                      <>
                        <div className="fw-bold">
                          <i className="ti ti-alert-triangle me-1" />
                          {msg.split("\n")[0]}
                        </div>
                        <div>{msg.split("\n")[1]}</div>
                      </>
                    ) : (
                      msg
                    )}
                  </div>
                )}

                <div className="mt-4">
                  <div className="mb-3">
                    <label htmlFor="userEmail" className="form-label">
                      Email address <span className="text-danger">*</span>
                    </label>
                    <div className="app-search">
                      <input
                        type="email"
                        className="form-control"
                        id="userEmail"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={handleKeyDown}
                        autoComplete="username"
                        required
                      />
                      <i className="ti ti-mail app-search-icon" />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="userPassword" className="form-label">
                      Password
                    </label>
                    <div className="input-group">
                      <input
                        className="form-control"
                        id="userPassword"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={handleKeyDown}
                        autoComplete="current-password"
                        required
                      />
                      <button
                        className="btn border"
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} />
                      </button>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input form-check-input-light fs-14"
                        type="checkbox"
                        id="rememberMe"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="rememberMe">
                        Remember Me
                      </label>
                    </div>

                    <a href="/forgot-password" className="link-offset-3 text-muted">
                      Forgot Password?
                    </a>
                  </div>

                  <div className="d-grid">
                    <button
                      type="button"
                      className="btn btn-primary fw-bold py-2"
                      onClick={login}
                      disabled={loading}
                    >
                      {loading ? "Verifying..." : "Verify Now"}
                    </button>
                  </div>
                </div>

                <p className="text-muted text-center mt-4 mb-0">
                  New here?{" "}
                  <a href="/access-request" className="link-offset-3 fw-bold">
                    Raise Access Request
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}